// src/pages/OpsRetryEditor.jsx
import { useLocation, useNavigate } from "react-router-dom";
import { useMemo, useState } from "react";
import AppShell from "../layout/AppShell";
import { ENV, tbPostJson } from "../api/client";
import "./OpsRetryEditor.css";

export default function OpsRetryEditor() {
    const nav = useNavigate();
    const loc = useLocation();
    const initialRows = useMemo(() => loc.state?.rows || [], [loc.state?.rows]);

    const [items, setItems] = useState(() =>
        initialRows.map((r) => ({
            ex_id: r.ex_id,
            stage: r.stage,
            status: r.status,
            error_code: r.error_code,
            error_message: r.error_message,
            npw_id: r.npw_id,
            // prefill editor with request_payload
            text: JSON.stringify(r.request_payload ?? {}, null, 2),
            err: "",
        }))
    );

    const retryUrl = ENV.EXCEPTION_RETRY_URL;

    const parseAll = () => {
        let ok = true;
        const next = items.map((it) => {
            try {
                const obj = it.text.trim() ? JSON.parse(it.text) : {};
                return { ...it, obj, err: "" };
            } catch (e) {
                ok = false;
                return { ...it, obj: null, err: e.message };
            }
        });
        setItems(next);
        return ok ? next : null;
    };

    const doRetry = async () => {
        const parsed = parseAll();
        if (!parsed) return;
        try {
            const payload = {
                // generic shape that most backends can accept; backend may ignore overrides if unsupported
                retries: parsed.map((it) => ({
                    ex_id: it.ex_id,
                    request_payload_overrides: it.obj || {},
                })),
            };
            await tbPostJson(retryUrl, payload);
            nav("/ops/exceptions");
        } catch (e) {
            console.error("[RetryEditor] retry error:", e);
            alert("Retry failed. See console for details.");
        }
    };

    return (
        <AppShell activeKey="/ops/exceptions">
            {/* NEW: full-screen cover that sits on top of the app chrome */}
            <div className="opsr-cover">
                <div className="opsr-wrap">
                    <div className="opsr-bar">
                        <h2>Retry Exceptions ({items.length})</h2>
                        <div className="opsr-actions">
                            <button className="opsr-btn" onClick={() => nav(-1)}>
                                Back
                            </button>
                            <button
                                className="opsr-btn primary"
                                onClick={doRetry}
                                disabled={!items.length}
                            >
                                Send Retry
                            </button>
                        </div>
                    </div>

                    {!items.length && (
                        <div className="opsr-empty">
                            No selected exceptions. Go back and select rows.
                        </div>
                    )}

                    <div className="opsr-list">
                        {items.map((it, idx) => (
                            <div key={it.ex_id} className="opsr-card">
                                <div className="opsr-head">
                                    <div className="opsr-title">
                                        <strong>#{it.ex_id}</strong>
                                        <span className="opsr-pill">{it.stage}</span>
                                        <span className={`opsr-badge st-${String(it.status || "").toLowerCase()}`}>
                                            {it.status}
                                        </span>
                                        {it.npw_id ? <span className="opsr-npw">NPW {it.npw_id}</span> : null}
                                    </div>
                                    <div className="opsr-meta">
                                        <span className="opsr-code">{it.error_code}</span>
                                        <span className="opsr-msg" title={it.error_message}>
                                            {it.error_message}
                                        </span>
                                    </div>
                                </div>

                                <label className="opsr-label">Request Payload (editable JSON)</label>
                                <textarea
                                    className={`opsr-text ${it.err ? "has-error" : ""}`}
                                    rows={10}
                                    spellCheck={false}
                                    value={it.text}
                                    onChange={(e) => {
                                        const v = e.target.value;
                                        setItems((list) => {
                                            const cp = [...list];
                                            cp[idx] = { ...cp[idx], text: v, err: "" };
                                            return cp;
                                        });
                                    }}
                                />
                                {it.err ? <div className="opsr-err">JSON error: {it.err}</div> : null}
                            </div>
                        ))}
                    </div>
                </div>
            </div>
        </AppShell>
    );
}
